/**
 * Order Review business logic — PURE, unit-testable functions mirroring the
 * spec's Screen 2 threshold table. The server owns the authoritative check;
 * this only drives the UI (running total, message, Send-enable). All money math
 * is done in integer CENTS parsed from decimal strings — never float — so the
 * classic 0.1 + 0.2 trap can't corrupt a total.
 */

export interface PricedItem {
  unit_price: string
  quantity: number
}

export interface ThresholdConfig {
  min_threshold: string
  override_floor: string
  dropship_fee: string
}

export type ThresholdStateName =
  | 'OK'
  | 'VIP_OVERRIDE'
  | 'BLOCKED_NON_VIP'
  | 'HARD_FLOOR'
  | 'EMPTY'

export interface ThresholdResult {
  state: ThresholdStateName
  message: string
}

/** Parse a decimal money string ("189.00", "189", "0.5") to integer cents. */
export function toCents(value: string): number {
  const clean = value.trim().replace(/[^0-9.]/g, '')
  const [whole, frac = ''] = clean.split('.')
  const fracPadded = (frac + '00').slice(0, 2)
  return Number(whole || '0') * 100 + Number(fracPadded)
}

/** Format integer cents back to a 2dp decimal string ("18900" -> "189.00"). */
export function centsToString(cents: number): string {
  const whole = Math.floor(cents / 100)
  const frac = cents % 100
  return `${whole}.${String(frac).padStart(2, '0')}`
}

/** Sum unit_price × quantity across selected items, in cents, as a 2dp string. */
export function computeTotal(selectedItems: PricedItem[]): string {
  const cents = selectedItems.reduce(
    (sum, item) => sum + toCents(item.unit_price) * item.quantity,
    0,
  )
  return centsToString(cents)
}

/** "$125" for whole dollars, "$124.99" otherwise — matches the spec's phrasing. */
function dollarsLabel(cents: number): string {
  const s = centsToString(cents)
  return s.endsWith('.00') ? `$${s.slice(0, -3)}` : `$${s}`
}

/**
 * Threshold decision + display message for the running-total bar. Message is
 * the descriptive clause; the component composes "Selected: N items | Total:
 * $T — {message}" around it (EMPTY renders the message standalone).
 */
export function thresholdState(
  total: string,
  thresholds: ThresholdConfig,
  isVip: boolean,
): ThresholdResult {
  const totalCents = toCents(total)
  const minCents = toCents(thresholds.min_threshold)
  const floorCents = toCents(thresholds.override_floor)
  const minLabel = dollarsLabel(minCents)
  const floorLabel = dollarsLabel(floorCents)

  if (totalCents === 0) {
    return { state: 'EMPTY', message: 'No items selected for dropship.' }
  }
  if (totalCents >= minCents) {
    return { state: 'OK', message: `✅ Meets ${minLabel} minimum` }
  }
  if (totalCents >= floorCents) {
    if (isVip) {
      return {
        state: 'VIP_OVERRIDE',
        message: `⚠️ Below ${minLabel}. VIP override available — enter a note to enable Send.`,
      }
    }
    return {
      state: 'BLOCKED_NON_VIP',
      message: `⛔ Below ${minLabel} minimum. Customer is not VIP — cannot dropship.`,
    }
  }
  return {
    state: 'HARD_FLOOR',
    message: `⛔ Below ${floorLabel} floor. Cannot dropship.`,
  }
}

/** Whether [Send to KH Dropship] is enabled for the current state/override. */
export function canSend(
  state: ThresholdStateName,
  overrideChecked: boolean,
  noteText: string,
  alreadySubmitted = false,
): boolean {
  // An order already submitted to KH can never be re-sent — the server would 409.
  if (alreadySubmitted) return false
  if (state === 'OK') return true
  if (state === 'VIP_OVERRIDE') return overrideChecked && noteText.trim().length > 0
  return false
}
